import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Resources } from '@erecruit/types';
import {
  CarouselWizard,
  CarouselWizardStep,
  Button,
  CarouselWizardProps,
  styled,
  Modal,
  ant,
  Flex,
  Icon,
  H1,
  H3,
  Text,
} from '@erecruit/ui-kit';
import { Intl } from '@erecruit/core';
import { ApproversList, ApproveRejectModal } from './components';
import { ApproveRejectModalForm } from './components/ApproveRejectModal';

const { FormattedMessage } = Intl;

const getStatus = (
  approvalPathStatus: Resources.ApprovalStatus,
): CarouselWizardProps['status'] => {
  switch (approvalPathStatus) {
    case Resources.ApprovalStatus.Approved:
    case Resources.ApprovalStatus.Skipped:
      return 'success';
    case Resources.ApprovalStatus.InProgress:
      return 'pending';
    default:
      return 'critical';
  }
};

const canDisplayActions = (
  approvalPath: Resources.ApprovalPath,
  userGuid: string,
  step: Resources.ApprovalPathStep,
) => {
  return (
    approvalPath.currentStep === step.id &&
    step.approvalStatus === Resources.ApprovalStatus.InProgress &&
    step.eligibleApprovers.some(({ id }) => id === userGuid)
  );
};

export const ApprovalPathStatuses: Record<
  Resources.ApprovalStatus,
  Intl.LocalizationKeysArray
> = {
  [Resources.ApprovalStatus.Approved]:
    messageIds.vms.approvalPath.status.approved,
  [Resources.ApprovalStatus.InProgress]:
    messageIds.vms.approvalPath.status.inProgress,
  [Resources.ApprovalStatus.Rejected]:
    messageIds.vms.approvalPath.status.rejected,
  [Resources.ApprovalStatus.Skipped]:
    messageIds.vms.approvalPath.status.skipped,
};

const generateSteps = (
  approvalPath: Resources.ApprovalPath,
  userGuid: string,
  onApproversClick: (step: number) => void,
  onApproveClick: () => void,
  onRejectClick: () => void,
): CarouselWizardStep[] => {
  return approvalPath.steps.map(step => ({
    onApproversClick: () => onApproversClick(step.id),
    title: step.name,
    changedOn: step.completedOn,
    stepStatus: getStatus(step.approvalStatus),
    status: <FormattedMessage id={ApprovalPathStatuses[step.approvalStatus]} />,
    actions: canDisplayActions(approvalPath, userGuid, step)
      ? [
          <Button key="0" onClick={onRejectClick}>
            <FormattedMessage id={messageIds.vms.approvalPath.reject} />
          </Button>,
          <Button key="1" type="primary" onClick={onApproveClick}>
            <FormattedMessage id={messageIds.vms.approvalPath.approve} />
          </Button>,
        ]
      : undefined,
  }));
};

export interface ApprovalPathProps {
  approvalPath?: Resources.ApprovalPath;
  userGuid: string;
  onApproveClick(approvalPathId: number, note: string): void;
  onRejectClick(
    approvalPathId: number,
    rejectionReasonId: number,
    note: string,
  ): void;
  rejectionReasons?: Resources.ApprovalRejectionReason[];
}

const ApprovalPathContainer = styled.div({
  display: 'inline-block',
  maxWidth: 400,
  margin: 'auto',
});

const ApproversListModal = styled(Modal)(({ theme: { newColors } }) => ({
  ...ant({
    '.modal-footer': {
      background: newColors.bgPagePrimary,
      padding: '20px 32px',
    },
    '.modal-header': {
      padding: 32,
    },
  }),
}));

export const ApprovalPath = ({
  approvalPath,
  userGuid,
  onApproveClick,
  onRejectClick,
  rejectionReasons,
}: ApprovalPathProps) => {
  const [pickedStep, setPickedStep] = useState<number>();
  const [isModalOpen, setIsOpen] = useState(false);
  const [steps, setSteps] = useState<CarouselWizardStep[]>([]);
  const [approveRejectModalType, setApproveRejectModalType] = useState<
    'Approve' | 'Reject' | undefined
  >();

  const stepsApproved = useMemo(() => {
    return approvalPath?.steps.reduce(
      (acc, { approvalStatus }) =>
        [
          Resources.ApprovalStatus.Approved,
          Resources.ApprovalStatus.Skipped,
        ].includes(approvalStatus)
          ? acc + 1
          : acc,
      0,
    );
  }, [approvalPath]);

  const handleSubmit = useCallback(
    ({ note, rejectionReasonId }: ApproveRejectModalForm) => {
      if (approvalPath?.id) {
        approveRejectModalType === 'Approve'
          ? onApproveClick(approvalPath.id, note)
          : onRejectClick(approvalPath.id, rejectionReasonId as number, note);
      }
    },
    [approvalPath, approveRejectModalType, onApproveClick, onRejectClick],
  );

  useEffect(() => {
    if (approvalPath) {
      setSteps(
        generateSteps(
          approvalPath,
          userGuid,
          stepId => {
            setPickedStep(stepId);
            setIsOpen(true);
          },
          () => setApproveRejectModalType('Approve'),
          () => setApproveRejectModalType('Reject'),
        ),
      );
      setPickedStep(approvalPath.currentStep);
    }
  }, [approvalPath, userGuid]);

  if (!approvalPath) {
    return null;
  }

  const status = getStatus(approvalPath.approvalStatus);
  const currentStep = approvalPath.steps.find(step => step.id === pickedStep);

  return (
    <>
      <ApprovalPathContainer>
        {approveRejectModalType && (
          <ApproveRejectModal
            type={approveRejectModalType}
            visible={!!approveRejectModalType}
            onCancel={() => setApproveRejectModalType(undefined)}
            onSubmit={handleSubmit}
            rejectionReasons={rejectionReasons}
          />
        )}
        <CarouselWizard
          activatedOn={approvalPath.startedOn}
          status={status}
          stepsApproved={stepsApproved || 0}
          title={approvalPath.name}
          steps={steps}
          defaultStep={approvalPath.steps.findIndex(
            step => step.id === approvalPath.currentStep,
          )}
        />
      </ApprovalPathContainer>
      {currentStep && (
        <ApproversListModal
          visible={isModalOpen}
          footer={
            <Flex gutter={2}>
              <Icon size="small" type="info" />
              <Text color="labelSecondary">
                <FormattedMessage
                  id={messageIds.vms.approvalPath.approversList.footerText}
                  values={{
                    number: currentStep.miminumApprovers,
                  }}
                />
              </Text>
            </Flex>
          }
          pageTitle={
            <React.Fragment>
              <H1 color="headerTertiary" align="center">
                <FormattedMessage
                  id={messageIds.vms.approvalPath.approversList.pageTitle}
                />
              </H1>
              <H3 align="center">
                <FormattedMessage
                  id={messageIds.vms.approvalPath.approversList.pageSubtitle}
                />
              </H3>
            </React.Fragment>
          }
          title={
            <React.Fragment>
              <H1>
                <FormattedMessage
                  id={messageIds.vms.approvalPath.approversList.title}
                />
              </H1>
              <H3>
                <FormattedMessage
                  id={messageIds.vms.approvalPath.approversList.subtitle}
                />
              </H3>
            </React.Fragment>
          }
          onCancel={() => setIsOpen(false)}
        >
          <ApproversList
            eligibleApprovers={currentStep.eligibleApprovers}
            pathStepActions={currentStep.actions}
          />
        </ApproversListModal>
      )}
    </>
  );
};

export default ApprovalPath;
